import { useState } from 'react';

/**
 * The user id to hand a commerce-core 0.24 CartManagementProvider: withheld
 * until that user's organization id is known, so both reach the provider
 * together.
 *
 * commerce-core keys the cart query by `user:<userId>` alone and reads a
 * user's cart only once it has both ids. A user id that arrives first — the
 * org is held back until the bridge token exists — makes it read the stored
 * guest cart under the user's key; when the org follows the key is unchanged,
 * so nothing refetches and the user's cart is never read, yet its clean-up
 * then deletes the stored guest cart id (N1). With the user id held back, the
 * org's arrival changes the key, so it reads the user's cart: commerce-core
 * merges the stored guest cart into it, and one already carried over (404, or
 * no `anonymousId`) falls back to the user's cart.
 *
 * Released once per user and kept: commerce-core holds on to the last org
 * when the prop drops back to undefined (a bridge token renewing mid-session),
 * and taking the user id away then would only blank the cart.
 */
export const useCartUserId = (
  userId: string | undefined,
  organizationId: string | undefined
): string | undefined => {
  const [releasedUserId, setReleasedUserId] = useState<string>();
  // Set during render so the user id and the org land in the same commit.
  if (userId && organizationId && releasedUserId !== userId) {
    setReleasedUserId(userId);
    return userId;
  }
  // A sign-out ends the release: signing back in is held again.
  if (!userId && releasedUserId) {
    setReleasedUserId(undefined);
  }
  return userId && releasedUserId === userId ? userId : undefined;
};
